import React, { useEffect, useState } from "react";
import { userService } from "../services/userService";
import apiClient from "../services/apiClient";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import { USER_ROLES } from "../config/authRoles";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Search, Filter, Download, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import EmployeeSalaryManager from "../components/employee/EmployeeSalaryManager";

const Employees = () => {
  const { user, hasRole } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterId, setFilterId] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    mobile: "",
    employeeId: "",
    designation: "",
    department: "",
    managerUserId: 0,
    joiningDate: new Date().toISOString().split("T")[0],
    orgId: 0,
    userRoles: [USER_ROLES.EMPLOYEE],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (value) => {
    setFormData((prev) => ({ ...prev, userRoles: [value] }));
  };

  const fetchEmployees = async () => {
    try {
      let data = [];
      if (hasRole([USER_ROLES.SUPER_ADMIN, USER_ROLES.TA_ADMIN, USER_ROLES.TA_USER])) {
        data = await userService.getAll();
      } else {
        data = await userService.getByOrg(user?.org_id || user?.orgId || user?.org?.id || 1);
      }
      
      const userEmail = user?.email || user?.username;
      data.sort((a, b) => {
        if (a.email === userEmail) return -1;
        if (b.email === userEmail) return 1;
        return 0;
      });

      setEmployees(data);
    } catch (apiError) {
      setError(apiError.userMessage || "Unable to load employees.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(API_ENDPOINTS.users.create, formData);
      setIsAddModalOpen(false);
      setFormData({
        username: "",
        password: "",
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        mobile: "",
        employeeId: "",
        designation: "",
        department: "",
        managerUserId: 0,
        joiningDate: new Date().toISOString().split("T")[0],
        orgId: 0,
        userRoles: [USER_ROLES.EMPLOYEE],
      });
      setLoading(true);
      await fetchEmployees();
    } catch (apiError) {
      setError(apiError.userMessage || "Failed to create employee");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = [
      emp.firstName,
      emp.middleName,
      emp.lastName,
      emp.email,
      emp.designation,
      emp.employeeId,
    ]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesId = filterId ? (emp.employeeId || '').toLowerCase().includes(filterId.toLowerCase()) : true;
    const matchesName = filterName ? `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase().includes(filterName.toLowerCase()) : true;
    const matchesEmail = filterEmail ? (emp.email || '').toLowerCase().includes(filterEmail.toLowerCase()) : true;
    const matchesMobile = filterMobile ? (emp.mobile || '').toLowerCase().includes(filterMobile.toLowerCase()) : true;

    return matchesSearch && matchesId && matchesName && matchesEmail && matchesMobile;
  });

  if (selectedEmployee) {
    return (
      <div className="space-y-6">
        <EmployeeSalaryManager
          employee={selectedEmployee}
          onBack={() => setSelectedEmployee(null)}
          onEmployeeUpdate={(updated) => {
            setEmployees((prev) =>
              prev.map((e) =>
                (e.id || e.employeeId) === (updated.id || updated.employeeId)
                  ? { ...e, ...updated }
                  : e
              )
            );
            setSelectedEmployee((prev) => ({ ...prev, ...updated }));
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employees</h1>
          <p className="text-gray-500 mt-1">Manage your employee database</p>
        </div>
        {hasRole([USER_ROLES.SUPER_ADMIN, USER_ROLES.TA_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.HR_ADMIN]) && (
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="mr-2 h-4 w-4" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Middle Name</Label>
                  <Input
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mobile</Label>
                  <Input
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employee ID</Label>
                  <Input
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Joining Date</Label>
                  <Input
                    type="date"
                    name="joiningDate"
                    value={formData.joiningDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    onValueChange={handleRoleChange}
                    defaultValue={formData.userRoles[0]}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(USER_ROLES).map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Manager User ID</Label>
                  <Input
                    type="number"
                    name="managerUserId"
                    value={formData.managerUserId}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Org ID</Label>
                  <Input
                    type="number"
                    name="orgId"
                    value={formData.orgId}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
                <Button type="submit">Add Employee</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Employees</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {employees.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {employees.filter((e) => e.enabled).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Departments</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {new Set(employees.map((e) => e.department).filter(Boolean)).size}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Disabled</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {employees.filter((e) => !e.enabled).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-6">
          {/* Search and Filters */}
          {/* Search and Filters */}
          <div className="flex flex-col space-y-4 mb-6">
            <div className="flex items-center space-x-2 flex-1 max-w-full">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Global search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </Button>
                <Button variant="outline" size="sm">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <Input
                placeholder="Filter by ID..."
                value={filterId}
                onChange={(e) => setFilterId(e.target.value)}
              />
              <Input
                placeholder="Filter by Name..."
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
              <Input
                placeholder="Filter by Email..."
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
              />
              <Input
                placeholder="Filter by Mobile..."
                value={filterMobile}
                onChange={(e) => setFilterMobile(e.target.value)}
              />
            </div>
          </div>

          {/* Employee Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Employee ID</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className="cursor-pointer hover:bg-blue-50/60 transition-colors"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-medium">
                          {employee.firstName?.charAt(0) || "U"}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {[
                              employee.firstName,
                              employee.middleName,
                              employee.lastName,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                          <p className="text-sm text-gray-500">
                            {employee.email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {employee.employeeId || employee.id}
                    </TableCell>
                    <TableCell>{employee.department}</TableCell>
                    <TableCell>{employee.designation}</TableCell>
                    <TableCell>
                      <Badge
                        variant={employee.enabled ? "default" : "secondary"}
                      >
                        {employee.enabled ? "active" : "disabled"}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 border-blue-200"
                          onClick={() => setSelectedEmployee(employee)}
                        >
                          <Eye className="h-4 w-4 mr-1.5" />
                          Select & Configure
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && (
            <p className="mt-4 text-sm text-gray-500">Loading employees...</p>
          )}
          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default Employees;
